import PortableText from "@sanity/block-content-to-react";
import createImageUrlBuilder from "@sanity/image-url";
import { createClient } from "next-sanity";
import useTranslation from "next-translate/useTranslation";
import React, { useEffect, useRef, useState } from "react";
import SanityHeadSeo from "../../../sanity/Components/SanityHeadSeo/SanityHeadSeo";
import EstimationHeader from "SanityComponents/CreditImmobilierComponents/EstimationHeader";
import Footer from "SanityComponents/Footer/Footer";
import DropdownBodyContent from "../DropdownBodyContent";
import Card from "../GuideCards";
import ShareAt from "../ShareAt";
import SimilaireCards from "../SimilaireCards";
import style from "./style.module.css";
import WidgetCard from "../WidgetCard";
import formatUrl, { externalUrl } from "utils/FormatUrl";
import Author from "../Author";
import { useRouter } from "next/router";

const client = createClient({
  apiVersion: "2023-01-01",
  dataset: "production" || "production",
  projectId: "n2i0cmq4" || "",
  useCdn: false,
});

export function urlFor(source) {
  return createImageUrlBuilder(client).image(source);
}

export const serializer = {
  types: {
    image: (props) => (
      <>
        <div>
          <img
            src={urlFor(props.node.asset)
              .format("webp")
              .auto("format")
              .fit("clip")
              .auto("format")
              .toString()}
            alt={props.node.alt}
            className={style.imagcontent}
          />
        </div>
        <figcaption>{props.node.caption}</figcaption>
      </>
    ),
  },
  marks: {
    color: ({ mark, children }) => (
      <span style={{ color: mark.hex }}>{children}</span>
    ),
    link: ({ children, mark }) =>
      mark.blank ? (
        <a
          style={{
            textDecoration: "underline",
            color: "#3498db",
            cursor: "pointer",
          }}
          href={formatUrl(mark.href)}
          target="_blank"
          rel={
            externalUrl(mark.href) ||
            mark.href?.includes("list") ||
            mark.href?.includes("map") ||
            mark.href?.includes("?") ||
            mark.href?.includes("conditions") ||
            mark.href?.includes("contact")
              ? "noindex, nofollow, noreferrer"
              : "dofollow"
          }
        >
          {children}
        </a>
      ) : (
        <a
          style={{
            textDecoration: "underline",
            color: "#3498db",
            cursor: "pointer",
          }}
          href={formatUrl(mark.href)}
          rel={
            externalUrl(mark.href) ||
            mark.href?.includes("list") ||
            mark.href?.includes("map") ||
            mark.href?.includes("?") ||
            mark.href?.includes("conditions") ||
            mark.href?.includes("contact")
              ? "noindex, nofollow, noreferrer"
              : "dofollow"
          }
        >
          {children}
        </a>
      ),
    aligntexte: ({ children, mark }) =>
      mark.position == "1" ? (
        <span>{children}</span>
      ) : mark.position == "2" ? (
        <span
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            textAlign: "center",
          }}
        >
          {children}
        </span>
      ) : (
        <span
          style={{
            display: "flex",
            justifyContent: "end",
            width: "100%",
            textAlign: "end",
          }}
        >
          {children}
        </span>
      ),
  },
};

function index({ data, poststitle, slug, page }) {
  const { t, lang } = useTranslation("LandingPage");
  const cardRef = useRef(null);
  const endCardRef = useRef(null);
  const { asPath } = useRouter();
  const [isFixed, setIsFixed] = useState(false);
  const [flexEnd, setFlexEnd] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const cardContainerEndPosition =
        endCardRef?.current?.getBoundingClientRect().top;
      const cardContainerPosition =
        cardRef?.current?.getBoundingClientRect().top + 340;
      setIsFixed(
        scrollPosition >= cardContainerPosition &&
          cardContainerEndPosition > 349
      );
      if (cardContainerEndPosition > 349) {
        setFlexEnd(false);
      } else {
        setFlexEnd(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className={style.header}>
      {data?.contents?.map((item, index) => {
        if (item._type == "lpheader") {
          return (
            <EstimationHeader
              title={item.titre ? item.titre[lang] : ""}
              subtitle={item.soustitre && item.soustitre[lang]}
              overlay={item.overlay_image && item.overlay_image}
              content={item.content && item.content}
              backgroundUrl={
                item.background_image &&
                urlFor(item.background_image)
                  .fit("fill")
                  .format("webp")
                  .auto("format")
                  .toString()
              }
              SearchBarInputButton={
                item.SearchBarInputButton ? item.SearchBarInputButton : null
              }
              t={t}
              lang={lang}
            />
          );
        } else {
          return (
            <div className={style.guideCardContainerNoSearchBar}>
              {/* {item.body && (
                <>
                  <PortableText
                    projectId="n2i0cmq4"
                    dataset="production"
                    blocks={item.body}
                    serializers={serializer}
                  />
                </>
              )} */}
              {item.bodeandsharecard && item.texte && item.author ? (
                <div className={style.postContent}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      maxWidth: "817px",
                    }}
                  >
                    {item.texte?.length > 0 && (
                      <>
                        {item.texte.map((e, index) => (
                          <>
                            {lang == "ar"
                              ? e?.article?.texte_ar && (
                                  <PortableText
                                    projectId="n2i0cmq4"
                                    dataset="production"
                                    blocks={e.article.texte_ar}
                                    serializers={serializer}
                                  />
                                )
                              : e?.article?.texte_fr && (
                                  <PortableText
                                    projectId="n2i0cmq4"
                                    dataset="production"
                                    blocks={e.article.texte_fr}
                                    serializers={serializer}
                                  />
                                )}

                            {e._type == "Faq" && (
                              <DropdownBodyContent
                                title={e.titre ? e.titre[lang] : null}
                                items={e.faq && e.faq}
                                lang={lang}
                              />
                            )}

                            {lang == "ar"
                              ? e?.plus_article?.body2_texte_ar && (
                                  <PortableText
                                    projectId="n2i0cmq4"
                                    dataset="production"
                                    blocks={e.plus_article.body2_texte_ar}
                                    serializers={serializer}
                                  />
                                )
                              : e?.plus_article?.body2_texte_fr && (
                                  <PortableText
                                    projectId="n2i0cmq4"
                                    dataset="production"
                                    blocks={e.plus_article.body2_texte_fr}
                                    serializers={serializer}
                                  />
                                )}
                            {/* {e.body && (
                                <PortableText
                                  projectId="n2i0cmq4"
                                  dataset="production"
                                  blocks={e.body}
                                  serializers={serializer}
                                />
                              )} */}
                            {/* {e._type == "Faq" && (
                                <DropdownBodyContent
                                  title={e.titre ? e.titre[lang] : null}
                                  items={e.faq}
                                  lang={lang}
                                />
                              )} */}
                            {/* {e.body2 && (
                                <PortableText
                                  projectId="n2i0cmq4"
                                  dataset="production"
                                  blocks={e.body2}
                                  serializers={serializer}
                                />
                              )} */}
                          </>
                        ))}
                      </>
                    )}
                    {item.author || item.publishedAt ? (
                      <>
                        <Author
                          image={
                            item.author?.image &&
                            urlFor(item.author.image)
                              .format("webp")
                              .auto("format")
                              .fit("clip")
                              .auto("format")
                              .toString()
                          }
                          name={item.author?.name && item.author.name[lang]}
                          date={
                            item.publishedAt?.publishedAt &&
                            item.publishedAt.publishedAt
                          }
                          bio={
                            item.author?.bio1 ? item.author.bio1[lang] : null
                          }
                          lang={lang}
                        />
                      </>
                    ) : null}
                  </div>
                  <div className={style.cardsComposant}>
                    {item.bodeandsharecard?.length > 0 &&
                      item.bodeandsharecard.map((elem, index) => (
                        <>
                          {elem._type == "ShareAt" && (
                            <>
                              <div className={style.cardPosts}>
                                <ShareAt
                                  title={elem.title ? elem.title[lang] : null}
                                  linkToShare={`https://agenz.ma${formatUrl(
                                    asPath,
                                    lang
                                  )}`}
                                  fb={
                                    elem.fbIcon &&
                                    urlFor(elem.fbIcon)
                                      .format("webp")
                                      .auto("format")
                                      .fit("clip")
                                      .auto("format")
                                      .toString()
                                  }
                                  wh={
                                    elem.whIcon &&
                                    urlFor(elem.whIcon)
                                      .format("webp")
                                      .auto("format")
                                      .fit("clip")
                                      .auto("format")
                                      .toString()
                                  }
                                  in={
                                    elem.inIcon &&
                                    urlFor(elem.inIcon)
                                      .format("webp")
                                      .auto("format")
                                      .fit("clip")
                                      .auto("format")
                                      .toString()
                                  }
                                  copy={
                                    elem.copyIcon &&
                                    urlFor(elem.copyIcon)
                                      .format("webp")
                                      .auto("format")
                                      .fit("clip")
                                      .auto("format")
                                      .toString()
                                  }
                                />
                              </div>
                            </>
                          )}
                          {elem.annoncesSimilaires &&
                            elem.annoncesSimilaires?.annoncesSimilaires
                              ?.length > 0 && (
                              <SimilaireCards
                                page={page && page}
                                title={
                                  elem?.annoncesSimilaires?.titre &&
                                  elem.annoncesSimilaires.titre[lang]
                                }
                                posts={
                                  elem?.annoncesSimilaires
                                    ?.annoncesSimilaires &&
                                  elem.annoncesSimilaires.annoncesSimilaires
                                }
                                lang={lang}
                              />
                            )}
                          {elem._type == "GuidePostWidget" ? (
                            <WidgetCard
                              title={elem.title ? elem.title[lang] : null}
                              subtitle={
                                elem.subtitle ? elem.subtitle[lang] : null
                              }
                              heading={elem.heading ? elem.heading[lang] : null}
                              button={elem.button ? elem.button[lang] : null}
                              buttonLink={
                                elem.link_button ? elem.link_button : null
                              }
                              lang={lang}
                            />
                          ) : null}
                        </>
                      ))}
                  </div>
                </div>
              ) : null}
              {item.GuidCreditPageCards &&
                item.GuidCreditPageCards?.GuidCreditPageCards?.length > 0 && (
                  <div
                    className={style.guideCardContainer}
                    style={{ top: "0px" }}
                    ref={endCardRef}
                  >
                    <div className={style.guideCardTitle}>
                      {item.GuidCreditPageCards?.titre && (
                        <p className={style.guidetitle}>
                          {item.GuidCreditPageCards?.titre[lang]}
                        </p>
                      )}

                      {item.GuidCreditPageCards?.button && (
                        <p className={style.guidebtn}>
                          <a
                            href={formatUrl(
                              item.GuidCreditPageCards?.buttonLink &&
                                item.GuidCreditPageCards?.buttonLink
                            )}
                            rel={
                              item.GuidCreditPageCards?.buttonLink?.includes(
                                "list"
                              ) ||
                              item.GuidCreditPageCards?.buttonLink?.includes(
                                "map"
                              ) ||
                              item.GuidCreditPageCards?.buttonLink?.includes(
                                "?"
                              ) ||
                              item.GuidCreditPageCards?.buttonLink?.includes(
                                "conditions"
                              ) ||
                              item.GuidCreditPageCards?.buttonLink?.includes(
                                "contact"
                              )
                                ? "noindex, nofollow, noreferrer"
                                : "dofollow"
                            }
                          >
                            {item.GuidCreditPageCards?.button[lang]}
                          </a>
                        </p>
                      )}
                    </div>

                    <div className={style.cards}>
                      {item.GuidCreditPageCards?.GuidCreditPageCards?.map(
                        (elem, index) => {
                          return (
                            <>
                              {elem && (
                                <a
                                  href={formatUrl(
                                    `/${page}/${elem?.slug?.current}`
                                  )}
                                  rel={
                                    elem?.slug?.current?.includes("list") ||
                                    elem?.slug?.current?.includes("map") ||
                                    elem?.slug?.current?.includes("?") ||
                                    elem?.slug?.current?.includes(
                                      "conditions"
                                    ) ||
                                    elem?.slug?.current?.includes("contact")
                                      ? "noindex, nofollow, noreferrer"
                                      : "dofollow"
                                  }
                                >
                                  <Card
                                    image={
                                      elem.mainImage &&
                                      urlFor(elem.mainImage)
                                        .width(404)
                                        .height(187)
                                        .format("webp")
                                        .auto("format")
                                        .fit("clip")
                                        .auto("format")
                                        .toString()
                                    }
                                    width={elem.width ? elem.width : 404}
                                    height={elem.height ? elem.height : 187}
                                    title={elem.title ? elem.title[lang] : ""}
                                    subtitle={
                                      elem.subtitle ? elem.subtitle[lang] : null
                                    }
                                  />
                                </a>
                              )}
                            </>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
              {item._type == "SanityHeadSeo" && (
                <SanityHeadSeo
                  title={item.titre ? item.titre[lang] : ""}
                  ogTitle={item.titre ? item.titre[lang] : ""}
                  description={item.description ? item.description[lang] : ""}
                  ogDescription={item.description ? item.description[lang] : ""}
                  canonicalUrl={item.canonicalUrl && item.canonicalUrl}
                  ogTwitterImage={
                    item.mainImage
                      ? urlFor(item.mainImage)
                          .width(250)
                          .height(250)
                          .format("webp")
                          .auto("format")
                          .fit("clip")
                          .auto("format")
                          .toString()
                      : null
                  }
                  ogType={item.ogType && item.ogType}
                  ogImageUrl={
                    item.mainImage
                      ? urlFor(item.mainImage)
                          .width(250)
                          .height(250)
                          .format("webp")
                          .auto("format")
                          .fit("clip")
                          .auto("format")
                          .toString()
                      : null
                  }
                  structuredData={null}
                >
                  <></>
                </SanityHeadSeo>
              )}
              {item._type == "SanityFooter" && (
                <>
                  <div className={style.footer}>
                    <div className={style.footerInner}>
                      <Footer
                        propos={item.propos && item.propos[lang]}
                        proposText={item.text ? item.text[lang] : ""}
                        donnee={item.donnees && item.donnees[lang]}
                        donneeText={item.textdonnee && item.textdonnee[lang]}
                        technologie={item.technologie && item.technologie[lang]}
                        technologieText={
                          item.texttechnologies && item.texttechnologies[lang]
                        }
                        logo={
                          item.agenz_logo &&
                          urlFor(item.agenz_logo)
                            .format("webp")
                            .auto("format")
                            .fit("clip")
                            .auto("format")
                            .toString()
                        }
                        facebook={
                          item.facebook_logo &&
                          urlFor(item.facebook_logo)
                            .format("webp")
                            .auto("format")
                            .fit("clip")
                            .auto("format")
                            .toString()
                        }
                        instagram_logo={
                          item.instagram_logo &&
                          urlFor(item.instagram_logo)
                            .format("webp")
                            .auto("format")
                            .fit("clip")
                            .auto("format")
                            .toString()
                        }
                        linked_in={
                          item.linkedin_logo &&
                          urlFor(item.linkedin_logo)
                            .format("webp")
                            .auto("format")
                            .fit("clip")
                            .auto("format")
                            .toString()
                        }
                        socialMediaText={
                          item.socialMediatext && item.socialMediatext
                        }
                        copyRightText={
                          item.permission_text && item.permission_text
                        }
                      />
                    </div>
                    {/* <div className={style.footerMobile}>
                              <MobileFooter/>
                          </div> */}
                  </div>
                </>
              )}
            </div>
          );
        }
      })}
    </div>
  );
}

export default index;
